import React, { useState, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface EquationEditorModalProps {
  show: boolean;
  onHide: () => void;
  onInsert: (html: string) => void;
}

/* Common LaTeX symbol groups for quick insertion */
const SYMBOL_GROUPS = [
  {
    label: 'Common',
    symbols: [
      { label: '÷', tex: '\\div' },
      { label: '×', tex: '\\times' },
      { label: '±', tex: '\\pm' },
      { label: '≠', tex: '\\neq' },
      { label: '≤', tex: '\\leq' },
      { label: '≥', tex: '\\geq' },
      { label: '≈', tex: '\\approx' },
      { label: '∞', tex: '\\infty' },
    ],
  },
  {
    label: 'Fractions & Powers',
    symbols: [
      { label: 'a/b', tex: '\\frac{a}{b}' },
      { label: 'x²', tex: 'x^{2}' },
      { label: 'xⁿ', tex: 'x^{n}' },
      { label: '√x', tex: '\\sqrt{x}' },
      { label: 'ⁿ√x', tex: '\\sqrt[n]{x}' },
      { label: 'x₁', tex: 'x_{1}' },
    ],
  },
  {
    label: 'Greek',
    symbols: [
      { label: 'α', tex: '\\alpha' },
      { label: 'β', tex: '\\beta' },
      { label: 'γ', tex: '\\gamma' },
      { label: 'δ', tex: '\\delta' },
      { label: 'θ', tex: '\\theta' },
      { label: 'λ', tex: '\\lambda' },
      { label: 'μ', tex: '\\mu' },
      { label: 'π', tex: '\\pi' },
      { label: 'σ', tex: '\\sigma' },
      { label: 'φ', tex: '\\phi' },
      { label: 'ω', tex: '\\omega' },
    ],
  },
  {
    label: 'Calculus',
    symbols: [
      { label: '∑', tex: '\\sum_{i=1}^{n}' },
      { label: '∫', tex: '\\int_{a}^{b}' },
      { label: '∂', tex: '\\partial' },
      { label: 'lim', tex: '\\lim_{x \\to a}' },
      { label: 'dx', tex: '\\frac{d}{dx}' },
      { label: '∏', tex: '\\prod_{i=1}^{n}' },
    ],
  },
  {
    label: 'Trig',
    symbols: [
      { label: 'sin', tex: '\\sin' },
      { label: 'cos', tex: '\\cos' },
      { label: 'tan', tex: '\\tan' },
      { label: 'log', tex: '\\log' },
      { label: 'ln', tex: '\\ln' },
    ],
  },
  {
    label: 'Sets',
    symbols: [
      { label: '∈', tex: '\\in' },
      { label: '∉', tex: '\\notin' },
      { label: '⊂', tex: '\\subset' },
      { label: '∪', tex: '\\cup' },
      { label: '∩', tex: '\\cap' },
      { label: '∅', tex: '\\emptyset' },
      { label: '∀', tex: '\\forall' },
      { label: '∃', tex: '\\exists' },
    ],
  },
  {
    label: 'Matrices',
    symbols: [
      { label: '[matrix]', tex: '\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}' },
      { label: '(matrix)', tex: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
    ],
  },
];

const EquationEditorModal: React.FC<EquationEditorModalProps> = ({ show, onHide, onInsert }) => {
  const [latex, setLatex] = useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const { previewHtml, previewError } = useMemo(() => {
    if (!latex.trim()) return { previewHtml: '', previewError: '' };
    try {
      return { previewHtml: katex.renderToString(latex, { throwOnError: true, displayMode: true }), previewError: '' };
    } catch (e: any) {
      return { previewHtml: '', previewError: e.message || 'Invalid LaTeX' };
    }
  }, [latex]);

  const insertSymbol = (tex: string) => {
    const ta = textareaRef.current;
    if (!ta) { setLatex(prev => prev + ' ' + tex); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = latex.substring(0, start);
    const after = latex.substring(end);
    const newTex = before + tex + after;
    setLatex(newTex);
    // Restore cursor after inserted text
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + tex.length;
    }, 0);
  };

  const handleInsert = () => {
    if (!latex.trim()) return;
    // Wrap in span with class math-tex so KaTeX auto-render picks it up
    const html = `<span class="math-tex">\\(${latex}\\)</span>`;
    onInsert(html);
    setLatex('');
    onHide();
  };

  const handleClose = () => {
    setLatex('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ background: 'var(--gradient-brand)', color: 'var(--text-on-brand)' }}>
        <Modal.Title style={{ fontSize: 16 }}>Insert Equation</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 20 }}>
        {/* Symbol buttons */}
        <div style={{ marginBottom: 14 }}>
          {SYMBOL_GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-faint)', fontWeight: 600, marginRight: 8 }}>{group.label}:</span>
              {group.symbols.map((s) => (
                <button
                  key={s.tex}
                  type="button"
                  onClick={() => insertSymbol(s.tex)}
                  style={{
                    display: 'inline-block', margin: '0 3px 4px 0', padding: '3px 8px',
                    border: '1px solid var(--border-default)', borderRadius: 4, background: 'var(--surface-page)',
                    cursor: 'pointer', fontSize: 13, fontFamily: 'serif',
                  }}
                  title={s.tex}
                >
                  {s.label}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* LaTeX input */}
        <Form.Group style={{ marginBottom: 14 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>LaTeX</Form.Label>
          <Form.Control
            as="textarea"
            ref={textareaRef}
            rows={3}
            value={latex}
            onChange={(e) => setLatex(e.target.value)}
            placeholder="e.g.  x = \frac{-b \pm \sqrt{b^2 - 4ac}}{2a}"
            style={{ fontFamily: 'monospace', fontSize: 14 }}
          />
        </Form.Group>

        {/* Preview */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontWeight: 600, fontSize: 13 }}>Preview</label>
          <div
            style={{
              minHeight: 60, padding: 16, border: '1px solid var(--border-subtle)', borderRadius: 8,
              background: 'var(--surface-page)', textAlign: 'center', fontSize: 18,
            }}
          >
            {previewError ? (
              <span style={{ color: 'var(--status-danger)', fontSize: 13 }}>{previewError}</span>
            ) : previewHtml ? (
              <span dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : (
              <span style={{ color: '#bbb', fontSize: 13 }}>Type LaTeX above to see preview</span>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleInsert} disabled={!latex.trim() || !!previewError}>
          Insert Equation
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EquationEditorModal;
